import React, { useEffect, useState } from "react";
import { Bounce, toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import { useAuth } from "../../../../context/AuthContext";
import classes from "./AddOrganizationLeadership.module.css";
import QuestionCard from "../../../ui/QuestionCard/QuestionCard";
import QuestionBox from "../../../ui/QuestionBox/QuestionBox";
import CommonFooter from "../../../CommonFooter/CommonFooter";
import ScoredInputRow from "../../../ui/ScoredInputRow/ScoredInputRow";
import { env } from "../../../../constants/env";
import QuestionSection from "../../../ui/QuestionSection/QuestionSection";
import { useCertification } from "../../../../hooks/useCertification";

// Points configuration for each question
const QUESTION_POINTS = {
  1.1: 10,
  "1.2a": 5,
  "1.2b": 5,
  "1.2c": 5,
  "1.2d": 5,
  "1.2e": 5,
  "1.2f": 5,
  "1.2g": 5,
  1.3: 10,
  1.4: 10,
  "1.5a": 5,
  "1.5b": 5,
  1.6: 10,
  1.7: 10,
  1.8: 10,
  1.9: 10,
  "1.10": 10,
};

const questionSets = [
  {
    render: (onAnswerChange, answers, editable) => (
      <>
        <QuestionSection
          number="2.1"
          title="Procedure and/or Process for Organizational Code of Conduct"
          questions={[
            {
              content:
                "Has the laboratory defined a procedure and/or a process that addresses, but is not limited to, the following?",
              hideOptions: true,
            },
            {
              content: "Adherence to organizational policies and procedures",
              number: "a",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Impartiality",
              number: "b",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Confidentialit",
              number: "c",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Conflicts of interest",
              number: "d",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="2.2"
          title="Implementation of the Organizational Code of Conduct"
          questions={[
            {
              content:
                "Has the laboratory implemented the procedure and/or process and does it have records of the following?",
              hideOptions: true,
            },
            {
              content: "Adherence to organizational policies and procedures;",
              number: "a",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Impartiality;",
              number: "b",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Confidentiality;",
              number: "c",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Conflicts of interest",
              number: "d",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="2.3"
          title="Deputization"
          questions={[
            {
              content:
                "In the event of the absence of key personnel, has the laboratory implemented a process to ensure the continuity of the laboratory management system?",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="2.4"
          title="Budgetary Projections"
          questions={[
            {
              content:
                "Are budgetary projections based on personnel needs, scope of test, infrastructure, equipment needs, service and maintenance and quality assurance process and materials (IQC and EQA)?",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="2.5"
          title="Routine Review of Quality and Technical Records"
          questions={[
            {
              content:
                "Does the laboratory routinely perform a documented review of all quality and technical records?",
              hideOptions: true,
            },
            {
              content: "Follow-up of action items from previous reviews;",
              number: "a",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Status of corrective actions taken and required risk mitigation actions;",
              number: "b",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Reports from personnel;",
              number: "c",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Environmental monitoring logs;",
              number: "d",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Sample rejection records;",
              number: "e",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Equipment calibration and maintenance records;",
              number: "f",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "IQC records across all test areas;",
              number: "g",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Outcomes of PTs and other forms of inter-laboratory comparisons;",
              number: "h",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Quality indicators;",
              number: "i",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Customer complaints and feedback;",
              number: "j",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Results of improvement projects;",
              number: "k",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Documentation of this routine review and action planning with personnel for resolution and follow-up review.",
              number: "l",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="2.6"
          title="Procedure and/or Process for Management Review"
          questions={[
            {
              content:
                "Has the laboratory defined a procedure and/or a process that addresses, but is not limited to, the following?",
              hideOptions: true,
            },
            {
              content: "Frequency of management reviews;",
              number: "a",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Review input (agenda as per Clause 8.9.2 of ISO15189:2022);",
              number: "b",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Key attendees;",
              number: "c",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Conduct of review activities;",
              number: "d",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Review output (decisions, actions to be taken, provision of required resources person responsible and due dates);",
              number: "e",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Communication of decisions and actions to be taken to the relevant persons;",
              number: "f",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Ensure all actions arising are completed within the defined timeframe",
              number: "g",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="2.7"
          title="Conduct of Management Reviews"
          questions={[
            {
              content:
                "Does the laboratory management perform a review and discussion of the laboratory management system at planned intervals?",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="2.8"
          title="Management Review Inputs"
          questions={[
            {
              content: (
                <>
                  Does the management review meeting include the following
                  inputs?
                  <br />
                  Note: The minimum list of review inputs should include the
                  requirements of Clause 8.9.2 (a-j) of ISO15189:2022.
                </>
              ),
              hideOptions: true,
            },
            {
              content:
                "Status of actions from previous management reviews, internal and external changes to the management system, changes in the volume and type of laboratory activities and adequacy of resources;",
              number: "a",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Fulfilment of objectives and suitability of policies and procedures;",
              number: "b",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Outcomes of recent evaluations, process monitoring using quality indicators, internal audits, analysis of non-conformities, corrective actions and assessments by external bodies;",
              number: "c",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Patient, user and personnel feedback and complaints;",
              number: "d",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Quality assurance of result validity;",
              number: "e",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Effectiveness of any implemented improvements and actions taken to address risks and opportunities for improvement;",
              number: "f",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Performance of external providers, including referral laboratories and technical consultants;",
              number: "g",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Results of participation in interlaboratory comparison programs;",
              number: "h",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Evaluation of POCT activities;",
              number: "i",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Other relevant factors, such as monitoring activities and training",
              number: "j",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="2.9"
          title="Management Review Outputs"
          questions={[
            {
              content: (
                <>
                  Does the management review meeting include the following
                  outputs?
                  <br />
                  Note: The interval between management reviews should be no
                  greater than 12 months; however, shorter intervals should be
                  adopted when a Laboratory Management System is being
                  established.
                </>
              ),
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Effectiveness of the management system and its processes;",
              number: "a",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Improvement of the laboratory activities related to the fulfilment of the requirements of this document;",
              number: "b",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Provision of required resources;",
              number: "c",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Improvement of services to patients and users;",
              number: "d",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Any need for change.",
              number: "e",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="2.10"
          title="Communication of Review Findings"
          questions={[
            {
              content:
                "Are findings and actions from routine technical and management review meeting communicated to the relevant personnel?",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />
        <QuestionSection
          number="2.11"
          title="Completion and Monitoring of Review Action Items"
          questions={[
            {
              content:
                "Does laboratory management ensure that actions from routine technical review and management review meetings are completed within defined timeframes and monitored for their effectiveness?",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />
      </>
    ),
  },
];

const AddOrganizationLeadership = () => {
  const navigate = useNavigate();
  const [currentSet, setCurrentSet] = useState(0);
  const { user, token } = useAuth();
  const [status, setStatus] = useState(null);

  const { labId, questionnaireId } = useParams(); // ✅ get questionnaireId too

  const editable = questionnaireId ? status === "Not Attached" : true;

  // --- Prefill answers if editing ---
  const {
    formData: answers,
    handleChange: handleAnswerChange,
    handleDone,
    editMode,
    loading,
    isFetching,
  } = useCertification(
    "Organization & Leadership", // section
    {}, // initialAnswers
    questionnaireId // questionnaireId (optional)
  );

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet(currentSet + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet(currentSet - 1);
  };

  return (
    <div
      className={`${classes.wrapper} ${!editMode ? classes.fromParams : ""}`}
    >
      <div
        className="d-flex align-items-center gap-2 mb-4"
        onClick={() => navigate(-1)}
        role="button"
      >
        <IoIosArrowRoundBack size={35} />
        <h2 className="m-0">
          Section 02: Organisation and Leadership{" "}
          {questionnaireId && "(Editing)"}
        </h2>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          {questionSets[currentSet].render(
            handleAnswerChange,
            answers,
            editMode
          )}

          {editMode && (
            <CommonFooter
              handlePrevious={handlePrevious}
              handleCancel={() => navigate(-1)}
              handleNext={handleNext}
              handleDone={handleDone}
              questionSets={questionSets}
              currentSet={currentSet}
              cancelText="Cancel"
              doneText={
                questionnaireId ? "Update Assessment" : "Submit Assessment"
              }
            />
          )}
        </>
      )}
    </div>
  );
};

export default AddOrganizationLeadership;
